import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/virtualizedTable";
import { createColumnHelper } from "@tanstack/react-table";
import { Users, Building, Plus } from "lucide-react";
import { BeaconAllocation } from "@/lib/api/fund";
import { formatCurrency } from "@/lib/utils";
import { LoadingIndicator } from "../components/LoadingIndicator";
import { Button } from "@/components/ui/button";
import { CreateAllocationModal } from "../components/CreateAllocationModal";
import { UpdateAllocationModal } from "../components/UpdateAllocationModal";
import { useQueryParam } from "@/hooks/use-query-param";

interface BeaconAllocationsTableProps {
  allocations: BeaconAllocation[];
  selectedYears: number[];
  isLoading: boolean;
}

export function BeaconAllocationsTable({ allocations, selectedYears, isLoading }: BeaconAllocationsTableProps) {
  const columnHelper = createColumnHelper<BeaconAllocation>();
  const [allocationId, setAllocationId] = useQueryParam("allocation_id", "");
  const [isCreating, setIsCreating] = useQueryParam("is_creating_allocation", "");

  const filteredAllocations = useMemo(() => {
    if (selectedYears.length === 0) return allocations;
    return allocations.filter(a => selectedYears.includes(a.year));
  }, [allocations, selectedYears]);

  const handleCellClick = (row: BeaconAllocation) => {
    setAllocationId(row.id);
  };

  const handleCreateClick = () => {
    setIsCreating("true");
  };

  const columns = [
    columnHelper.accessor("year", {
      id: "year",
      header: "Year",
      enableColumnFilter: false,
      meta: { isCellClick: true },
      cell: (info) => (
        <Badge variant="outline">{info.getValue()}</Badge>
      ),
    }),
    columnHelper.accessor("created_at", {
      id: "date",
      header: "Date",
      enableColumnFilter: false,
      meta: { isCellClick: true },
      cell: (info) => (
        <div className="text-sm">
          {new Date(info.getValue()).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </div>
      ),
    }),
    columnHelper.display({
      id: "type",
      header: "Type",
      meta: { isCellClick: true },
      cell: (info) => {
        const hasEmployee = !!info.row.original.employee_id;
        return (
          <div className="flex items-center gap-2">
            {hasEmployee ? (
              <>
                <Users className="w-4 h-4 text-muted-foreground" />
                <span>Employee</span>
              </>
            ) : (
              <>
                <Building className="w-4 h-4 text-muted-foreground" />
                <span>Customer</span>
              </>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("Customer", {
      id: "source",
      header: "Source",
      meta: { isCellClick: true },
      ...({ accessorFn: (row: any) => row.Customer?.long_name || row.Employee?.name }),
      cell: (info: any) => {
        return (
          <div>
            {info.getValue() ||  "-"}
          </div>
        );
      },
    }),
    columnHelper.accessor("amount_allocated", {
      id: "allocated",
      header: "Allocated Amount",
      meta: { isCellClick: true },
      cell: (info) => (
        <div className="font-medium">
          {formatCurrency(parseFloat(info.getValue() || "0"))}
        </div>
      ),
    }),
    columnHelper.accessor("is_discretionary", {
      id: "discretionary",
      header: "Discretionary",
      meta: { isCellClick: true },
      ...({ accessorFn: (row: any) => row.is_discretionary ? "Discretionary" : "Standard" }),
      cell: (info: any) => {
        const isDiscretionary = info.row.original.is_discretionary;
        return (
          isDiscretionary ? (
            <Badge variant="secondary">Discretionary</Badge>
          ) : (
            <Badge variant="outline">Standard</Badge>
          )
        )
      },
    }),
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Allocations <LoadingIndicator isLoading={isLoading} /></CardTitle>
            <CardDescription className="text-sm">
              Track all fund allocations and matching contributions
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={handleCreateClick}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Allocation
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <DataTable
          onCellClick={(row) => handleCellClick(row)}
          defaultSorting={[{ id: "year", desc: true }]}
          columns={columns as any[]}
          data={filteredAllocations}
          height="calc(100vh - 380px)"
          emptyResultsMessage={isLoading ? "Loading rows..." : "No results."}
        />
      </CardContent>
      
      <CreateAllocationModal />
      <UpdateAllocationModal allocations={allocations} />
    </Card>
  );
}
